import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { PageHeader, Loading, EmptyState, ErrorState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FilePenLine, CheckCircle2, AlertTriangle, Clock, Eye, Download, Loader2, Info, ExternalLink, MessageCircleQuestion, Ban, FileText } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const FILE_TIMEOUT = 120000;
const fmtDate = (s) => (s ? new Date(s.length === 10 ? `${s}T00:00:00` : s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const fmtDateTime = (s) => (s ? new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

const STATUS = {
  "Not opened": { cls: "bg-amber-100 text-amber-800", icon: Clock, label: "Please read & respond" },
  Opened: { cls: "bg-amber-100 text-amber-800", icon: Clock, label: "Please respond" },
  Overdue: { cls: "bg-red-100 text-red-800", icon: AlertTriangle, label: "Overdue — please respond" },
  "Query raised": { cls: "bg-sky-100 text-sky-800", icon: MessageCircleQuestion, label: "Your question is with HR" },
  Accepted: { cls: "bg-emerald-100 text-emerald-800", icon: CheckCircle2, label: "Accepted" },
  Acknowledged: { cls: "bg-emerald-100 text-emerald-800", icon: CheckCircle2, label: "Acknowledged" },
  Withdrawn: { cls: "bg-slate-100 text-slate-500", icon: Ban, label: "Withdrawn by HR" },
};
const isWaiting = (l) => ["Not opened", "Opened", "Overdue"].includes(l.display_status);

async function saveAcceptedCopy(l) {
  const t = toast.loading("Preparing your copy…");
  try {
    const res = await api.get(`/my/letters/${l.id}/accepted-copy`, { responseType: "blob", timeout: FILE_TIMEOUT });
    const cd = res.headers["content-disposition"] || "";
    const m = cd.match(/filename="([^"]+)"/);
    const href = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = href; a.download = m ? m[1] : `${l.letter_no}.pdf`; a.click();
    setTimeout(() => URL.revokeObjectURL(href), 5000);
    toast.success("Downloaded", { id: t });
  } catch { toast.error("Couldn't download — try again", { id: t }); }
}

function LetterDialog({ letter, nameOnRecord, onClose }) {
  const qc = useQueryClient();
  const [url, setUrl] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [typed, setTyped] = useState("");
  const [remarks, setRemarks] = useState("");
  const [asking, setAsking] = useState(false);
  const [question, setQuestion] = useState("");

  useEffect(() => {
    if (!letter) return undefined;
    let href = null;
    let cancelled = false;
    setUrl(null); setLoadError(false); setAgreed(false); setTyped(""); setRemarks(""); setAsking(false); setQuestion("");
    // Opening the file is what records "read" on the server — Accept is refused until this has happened.
    api.get(`/my/letters/${letter.id}/file`, { responseType: "blob", timeout: FILE_TIMEOUT })
      .then((res) => {
        if (cancelled) return;
        href = URL.createObjectURL(res.data);
        setUrl(href);
        qc.invalidateQueries({ queryKey: ["myLetters"] });
      })
      .catch(() => !cancelled && setLoadError(true));
    return () => { cancelled = true; if (href) setTimeout(() => URL.revokeObjectURL(href), 1000); };
  }, [letter, qc]);

  const done = () => {
    qc.invalidateQueries({ queryKey: ["myLetters"] });
    qc.invalidateQueries({ queryKey: ["myLetterStatus"] });
    qc.invalidateQueries({ queryKey: ["myActionCounts"] });
  };
  const respond = useMutation({
    mutationFn: async (body) => (await api.post(`/my/letters/${letter.id}/respond`, { version: letter.version, ...body }, { timeout: FILE_TIMEOUT })).data,
    onSuccess: (d, body) => {
      toast.success(body.action === "query" ? "Your question has been sent to HR" :
        `Thank you — ${d.status === "Acknowledged" ? "acknowledgement" : "acceptance"} recorded. Your copy is saved here.`);
      done(); onClose();
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  if (!letter) return null;
  const isImage = ["png", "jpg", "jpeg", "webp"].includes(letter.ext);
  const waiting = isWaiting(letter);
  const ack = letter.response === "acknowledge";

  return (
    <Dialog open={!!letter} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-4xl max-h-[94vh] overflow-y-auto flex flex-col p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="pr-6">{letter.title} <span className="text-slate-400 font-normal text-base">{letter.letter_no}{letter.version > 1 ? ` · v${letter.version}` : ""}</span></DialogTitle>
          <DialogDescription>
            {letter.letter_type} · from {letter.issued_by_name} on {fmtDate(letter.issued_at)}
            {waiting && <> · respond by <span className="font-medium">{fmtDate(letter.due_date)}</span></>}
          </DialogDescription>
        </DialogHeader>
        {letter.message && <p className="text-sm text-slate-700 rounded-md bg-slate-50 border border-slate-200 px-3 py-2 -mt-1"><span className="font-medium">Note from HR:</span> {letter.message}</p>}

        <div className="h-[52vh] sm:h-[58vh] shrink-0 rounded-md border border-slate-200 bg-slate-50 overflow-hidden">
          {loadError ? (
            <div className="h-full grid place-items-center text-center p-6"><div><AlertTriangle className="h-8 w-8 text-red-400 mx-auto mb-2" /><p className="text-sm text-slate-600">Couldn't open the letter. Please close this and try again.</p></div></div>
          ) : !url ? (
            <div className="h-full grid place-items-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : isImage ? <div className="h-full overflow-auto"><img src={url} alt={letter.title} className="w-full" /></div>
            : <iframe title={letter.title} src={url} className="w-full h-full bg-white" />}
        </div>
        {url && <p className="text-[11px] text-slate-400 -mt-1">Letter not showing properly on your phone? <a href={url} target="_blank" rel="noreferrer" className="text-teal underline inline-flex items-center gap-0.5">Open it in a new tab <ExternalLink className="h-3 w-3" /></a> or <a href={url} download={letter.file_name} className="text-teal underline">download it</a>.</p>}

        {(letter.thread || []).length > 0 && (
          <div className="max-h-28 overflow-y-auto space-y-1.5 text-sm">
            {letter.thread.map((m) => (
              <p key={m.id} className={cn("rounded px-2.5 py-1.5", m.by === "hr" ? "bg-teal/5" : "bg-amber-50")}>
                <span className="font-medium">{m.by === "hr" ? "HR" : "You"}</span> <span className="text-[11px] text-slate-400">{fmtDateTime(m.at)}</span>: {m.text}
              </p>
            ))}
          </div>
        )}

        {waiting && !asking && (
          <div className="rounded-md border border-teal/30 bg-teal/5 p-3 space-y-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox checked={agreed} disabled={!url} onCheckedChange={(v) => setAgreed(!!v)} className="mt-0.5" data-testid="letter-agree" />
              <span className="text-sm text-slate-700">{letter.statement}</span>
            </label>
            <div className="grid gap-3 sm:grid-cols-2">
              <div><Label className="text-xs">Type your full name to sign *</Label>
                <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={nameOnRecord} className="mt-1" disabled={!url} data-testid="letter-typed-name" autoComplete="off" />
                <p className="text-[11px] text-slate-400 mt-0.5">As on your HR record: {nameOnRecord}</p></div>
              <div><Label className="text-xs">Remarks (optional)</Label>
                <Input value={remarks} onChange={(e) => setRemarks(e.target.value)} className="mt-1" disabled={!url} maxLength={1000} /></div>
            </div>
            <div className="flex flex-col-reverse sm:flex-row sm:items-center justify-between gap-3">
              <button className="text-sm text-teal underline disabled:opacity-50" disabled={!url} onClick={() => setAsking(true)}>Something is wrong? Ask HR instead</button>
              <Button className="bg-navy w-full sm:w-auto" disabled={!url || !agreed || !typed.trim() || respond.isPending} data-testid="letter-accept"
                onClick={() => respond.mutate({ action: "accept", confirm: true, typed_name: typed, remarks })}>
                {respond.isPending ? "Saving…" : ack ? "I Acknowledge" : "I Accept"}
              </Button>
            </div>
            <p className="text-[11px] text-slate-400">Your name, date & time, IP address and device will be recorded with the letter.</p>
          </div>
        )}
        {waiting && asking && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-2">
            <Label className="text-xs">Your question for HR *</Label>
            <Textarea rows={3} value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="e.g. The extension date should be 31 March 2027, not 30 March." maxLength={2000} />
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" onClick={() => setAsking(false)}>Back</Button>
              <Button className="bg-navy" disabled={question.trim().length < 5 || respond.isPending} onClick={() => respond.mutate({ action: "query", query: question })}>Send to HR</Button>
            </div>
          </div>
        )}
        {letter.display_status === "Query raised" && <p className="text-sm text-sky-800 flex items-center gap-1.5"><MessageCircleQuestion className="h-4 w-4" /> Your question is with HR. You'll get a notification when they reply.</p>}
        {letter.has_accepted_copy && (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-emerald-700 flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> You {letter.status === "Acknowledged" ? "acknowledged" : "accepted"} this on {fmtDateTime(letter.responded_at)}.</p>
            <Button size="sm" className="bg-teal hover:bg-teal/90 text-white" onClick={() => saveAcceptedCopy(letter)}><Download className="h-4 w-4 mr-1" /> Download my copy</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function MyLetters() {
  const [reading, setReading] = useState(null);
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ["myLetters"], queryFn: async () => (await api.get("/my/letters")).data });
  const list = data?.letters || [];
  const todo = list.filter(isWaiting);

  return (
    <div>
      <PageHeader overline="From HR" title="My Letters" icon={FilePenLine}
        subtitle="Letters HR has sent you — contract extensions, appointment, increment and others. Read and accept them here; no printing or scanning." />
      {isLoading ? <Loading /> : isError ? <ErrorState message="Couldn't load your letters." onRetry={refetch} /> : list.length === 0 ? (
        <EmptyState icon={FileText} title="No letters yet" hint="When HR sends you a letter, you'll get a notification and it will appear here." />
      ) : (<>
        {todo.length > 0 ? (
          <div className={cn("mb-5 flex items-start gap-3 rounded-lg border px-4 py-3 text-sm",
            todo.some((l) => l.display_status === "Overdue") ? "border-red-200 bg-red-50 text-red-800" : "border-amber-200 bg-amber-50 text-amber-800")}>
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <p>You have <span className="font-semibold">{todo.length}</span> {todo.length === 1 ? "letter" : "letters"} to read and respond to. Open it, read it fully, type your name and accept — or ask HR if something is wrong.</p>
          </div>
        ) : (
          <div className="mb-5 flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" /><p>You're up to date. Your accepted letters are below — you can download your copy any time.</p>
          </div>
        )}
        <div className="mb-4 flex items-start gap-2 text-xs text-slate-500"><Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <p>Accepting here is the same as signing a printed copy. If anything in a letter is wrong, use "Ask HR instead" before accepting.</p></div>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {list.map((l) => {
            const s = STATUS[l.display_status] || STATUS.Opened;
            const Icon = s.icon;
            const waiting = isWaiting(l);
            return (
              <Card key={l.id} className={cn("p-4 shadow-none flex flex-col gap-3", l.display_status === "Overdue" ? "border-red-200" : waiting ? "border-amber-200" : "border-slate-200", l.status === "Withdrawn" && "opacity-60")} data-testid={`my-letter-${l.letter_no}`}>
                <div>
                  <p className="overline text-slate-400">{l.letter_type}</p>
                  <p className="font-semibold text-navy leading-snug">{l.title}</p>
                  <p className="text-[11px] text-slate-400">{l.letter_no}{l.version > 1 ? ` · revised v${l.version}` : ""} · {fmtDate(l.issued_at)}</p>
                </div>
                {l.message && <p className="text-sm text-slate-600 line-clamp-2">{l.message}</p>}
                {l.status === "Withdrawn" && <p className="text-xs text-slate-500">Withdrawn: {l.withdraw_reason}</p>}
                <div className="mt-auto flex items-end justify-between gap-2 pt-2 border-t border-slate-100">
                  <div>
                    <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold", s.cls)}><Icon className="h-3 w-3" />{s.label}</span>
                    <p className="text-[11px] text-slate-400 mt-1">{l.responded_at ? `on ${fmtDate(l.responded_at)}` : waiting ? `By ${fmtDate(l.due_date)}` : ""}</p>
                  </div>
                  {l.status !== "Withdrawn" && (
                    l.has_accepted_copy
                      ? <div className="flex gap-1.5">
                          <Button size="sm" variant="outline" onClick={() => setReading(l)}><Eye className="h-4 w-4" /></Button>
                          <Button size="sm" className="bg-teal hover:bg-teal/90 text-white" onClick={() => saveAcceptedCopy(l)}><Download className="h-4 w-4 mr-1" /> My copy</Button>
                        </div>
                      : <Button size="sm" className={waiting ? "bg-navy" : "bg-teal hover:bg-teal/90 text-white"} onClick={() => setReading(l)} data-testid={`open-letter-${l.letter_no}`}>
                          <Eye className="h-4 w-4 mr-1" /> {waiting ? (l.response === "acknowledge" ? "Read & Acknowledge" : "Read & Accept") : "Read"}
                        </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      </>)}
      {/* `reading` is the object captured on click, so it stays the same reference while the list refreshes. */}
      <LetterDialog letter={reading} nameOnRecord={data?.name_on_record} onClose={() => setReading(null)} />
    </div>
  );
}
